import React from 'react';
import { X, Clock, Trash2, CheckCircle, Image, Zap, Settings2, Video } from 'lucide-react';

const PromptHistoryPanel = ({
  isOpen,
  onClose,
  historyItems,
  onUsePrompt,
  onDeletePrompt,
  isLoading,
  totalGenerations,
  readOnly = false
}) => {

  // Mapping für deutsche Labels
  const getLabelForValue = (category, value) => {
    const labels = {
      time_of_day: {
        'sunrise': 'Sonnenaufgang',
        'morning': 'Morgen',
        'noon': 'Mittag',
        'afternoon': 'Nachmittag',
        'sunset': 'Sonnenuntergang',
        'golden_hour': 'Goldene Stunde',
        'blue_hour': 'Blaue Stunde',
        'evening': 'Abend',
        'night': 'Nacht',
        'midnight': 'Mitternacht'
      },
      weather: {
        'clear': 'Klar',
        'cloudy': 'Bewölkt',
        'overcast': 'Bedeckt',
        'foggy': 'Nebelig',
        'rainy': 'Regnerisch',
        'stormy': 'Stürmisch',
        'snowy': 'Schneefall'
      },
      camera: {
        '24mm': '24mm',
        '35mm': '35mm',
        '50mm': '50mm',
        '85mm': '85mm',
        'drone': 'Drohne',
        'top': 'Von oben',
        'low': 'Von unten'
      },
      style: {
        'cinematic': 'Cinematic',
        'action': 'Action',
        'editorial': 'Editorial',
        'vintage': 'Vintage',
        'documentary': 'Doku',
        'fashion': 'Fashion',
        'artistic': 'Künstlerisch'
      },
      substyle: {
        'standard': 'Standard',
        'epic': 'Episch',
        'moody': 'Moody',
        'bright': 'Hell',
        'dramatic': 'Dramatisch'
      },
      image_format: {
        'square_1_1': 'Quadrat 1:1',
        'portrait_9_16': 'Portrait 9:16',
        'portrait_4_5': 'Portrait 4:5',
        'landscape_16_9': 'Landscape 16:9',
        'landscape_3_2': 'Landscape 3:2'
      },
      body_framing: {
        'none': null,  // Don't show "Auto"
        'full_body': 'Ganzkörper',
        'half_body': 'Halbkörper',
        'portrait': 'Portrait',
        'upper_body': 'Oberkörper'
      }
    };

    return labels[category]?.[value] || value;
  };

  // Video model display names
  const getVideoModelLabel = (model) => {
    if (!model) return 'Video';
    if (model === 'kino_modus' || model === 'cinematic_video') return 'Kino';
    if (model === 'grok' || model === 'grok_imagine') return 'Shorts';
    if (model.startsWith('seedance') || model.startsWith('kling') || model.startsWith('veo') || model.startsWith('ltx')) return 'Shorts';
    return 'Video';
  };

  const getGenerationTypeLabel = (item) => {
    if (item.generation_type === 'remix' || item.prompt_source === 'remix') return 'Remix';
    if (item.generation_type === 'video') return getVideoModelLabel(item.video_model);
    return 'Foto';
  };

  const getGenerationTypeColor = (item) => {
    const label = getGenerationTypeLabel(item);
    if (label === 'Kino') return 'bg-amber-500/15 border-amber-500/30 text-amber-300';
    if (label === 'Remix') return 'bg-cyan-500/15 border-cyan-500/30 text-cyan-300';
    if (label === 'Shorts') return 'bg-purple-500/15 border-purple-500/30 text-purple-300';
    return 'bg-[#10B981]/15 border-[#10B981]/30 text-[#10B981]';
  };

  // Build dynamic chip list from actual settings - only show chips with real values
  const getActiveChips = (item) => {
    const chips = [];
    const settings = item.settings || {};
    const isVideo = item.generation_type === 'video';

    // Video-Eintrag: Spezielle Chips
    if (isVideo) {
      chips.push({ label: 'Video', icon: null, category: 'video' });
      if (item.video_model) {
        chips.push({ label: getVideoModelLabel(item.video_model), icon: null, category: 'model' });
      }
      return chips;
    }

    // Prompt-Quelle (Clean vs Advanced vs Julia)
    if (item.prompt_source === 'advanced' || settings.advanced_mode) {
      chips.push({ label: 'Erweitert', icon: null, category: 'mode' });
    } else if (item.prompt_source === 'julia') {
      chips.push({ label: 'Julia AI', icon: null, category: 'mode' });
    } else if (item.prompt_source === 'clean') {
      chips.push({ label: 'Clean', icon: null, category: 'mode' });
    }

    // Bildanzahl
    const numImages = item.num_images || 1;
    chips.push({ label: `${numImages} ${numImages === 1 ? 'Bild' : 'Bilder'}`, icon: null, category: 'count' });

    // Format - always present
    if (settings.image_format) {
      chips.push({ label: getLabelForValue('image_format', settings.image_format), icon: null, category: 'format' });
    }

    // Camera - only when explicitly set (not 'none' or null)
    if (settings.camera && settings.camera !== 'none') {
      chips.push({ label: getLabelForValue('camera', settings.camera), icon: null, category: 'camera' });
    }

    // Body Framing - only when explicitly set (not 'none')
    if (settings.body_framing && settings.body_framing !== 'none') {
      const framingLabel = getLabelForValue('body_framing', settings.body_framing);
      if (framingLabel) {
        chips.push({ label: framingLabel, icon: null, category: 'framing' });
      }
    }

    // Reference image indicator
    if (settings.reference_image_used) {
      chips.push({ label: 'Referenzbild', icon: null, category: 'reference' });
    }

    // Advanced-only chips: only show if they have actual non-null values
    if (settings.advanced_mode || item.prompt_source === 'advanced') {
      if (settings.time_of_day) {
        chips.push({ label: getLabelForValue('time_of_day', settings.time_of_day), icon: null, category: 'time' });
      }
      if (settings.weather) {
        chips.push({ label: getLabelForValue('weather', settings.weather), icon: null, category: 'weather' });
      }
      if (settings.style) {
        chips.push({ label: getLabelForValue('style', settings.style), icon: null, category: 'style' });
      }
      if (settings.substyle && settings.substyle !== 'standard') {
        chips.push({ label: getLabelForValue('substyle', settings.substyle), icon: null, category: 'substyle' });
      }
    }

    return chips;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/60 z-50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed inset-x-0 bottom-0 z-50 animate-slide-up">
        <div className="bg-black border-t-4 border-[#10B981] rounded-t-3xl shadow-2xl max-h-[80vh] flex flex-col">

          {/* Header */}
          <div className="sticky top-0 bg-black/95 backdrop-blur-xl border-b border-white/10 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#10B981]/20 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#10B981]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Prompt History</h3>
                <p className="text-xs text-white/50">
                  {isLoading ? 'Lädt...' : `${historyItems.length} ${historyItems.length === 1 ? 'Eintrag' : 'Einträge'}`}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-all"
              data-testid="close-history-panel">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-3 min-h-0" style={{ touchAction: 'pan-y', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
            {isLoading ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                <p className="text-white/50 text-sm">Lade History...</p>
              </div>
            ) : historyItems.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-white/30" />
                </div>
                <p className="text-white/60 text-base mb-2">Keine History vorhanden</p>
                <p className="text-white/40 text-sm">Erstelle dein erstes Bild, um die History zu füllen!</p>
              </div>
            ) : (
              historyItems.map((item, index) => {
                const activeChips = getActiveChips(item);
                const costSnaps = item.cost_snaps || 0;
                const isVideo = item.generation_type === 'video';

                return (
                <div
                  key={item.id}
                  className={`bg-gradient-to-br from-white/5 to-white/[0.02] border rounded-xl p-4 transition-all ${
                    isVideo
                      ? 'border-purple-500/30 hover:border-purple-500/50'
                      : 'border-white/10 hover:border-[#10B981]/50'
                  }`}
                  data-testid={`history-item-${item.id}`}
                >
                  {/* Header: Generationsnummer + Typ + Kosten */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 border rounded text-xs font-bold flex items-center gap-1 ${
                        isVideo
                          ? 'bg-purple-500/20 border-purple-500/40 text-purple-300'
                          : 'bg-[#10B981]/20 border-[#10B981]/40 text-[#10B981]'
                      }`}>
                        {isVideo && <Video className="w-3 h-3" />}
                        #{totalGenerations - index}
                      </span>
                      <span className="text-white/40 text-xs">
                        {new Date(item.created_at + (String(item.created_at).includes('Z') || String(item.created_at).includes('+') ? '' : 'Z')).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`px-2 py-0.5 border rounded text-xs font-bold ${getGenerationTypeColor(item)}`}>
                        {getGenerationTypeLabel(item)}
                      </span>
                      {costSnaps > 0 && (
                        <span className="px-2 py-0.5 bg-white/5 border border-white/10 rounded text-white/50 text-xs">
                          {costSnaps} SNAP{costSnaps !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Prompt Text */}
                  <div className="mb-3">
                    <p className="text-white font-semibold text-base leading-relaxed">
                      {item.user_prompt ? (
                            item.user_prompt.length > 200
                              ? `${item.user_prompt.substring(0, 200)}...`
                              : item.user_prompt
                          ) : (
                            '(Kein Text)'
                          )}
                     </p>
                  </div>

                  {/* Dynamic Settings Chips - nur tatsächlich gesetzte Werte */}
                  {activeChips.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {activeChips.map((chip, chipIdx) => (
                      <span
                        key={chipIdx}
                        className={`px-2.5 py-1 backdrop-blur-sm border rounded-lg text-xs font-medium transition-all ${
                          chip.category === 'video'
                            ? 'bg-purple-500/15 border-purple-500/40 text-purple-300'
                            : chip.category === 'model'
                            ? 'bg-indigo-500/10 border-indigo-500/30 text-indigo-300'
                            : chip.category === 'mode'
                            ? 'bg-purple-500/10 border-purple-500/30 text-purple-300'
                            : chip.category === 'count'
                            ? 'bg-blue-500/10 border-blue-500/30 text-blue-300'
                            : chip.category === 'reference'
                            ? 'bg-orange-500/10 border-orange-500/30 text-orange-300'
                            : 'bg-white/5 border-white/20 text-white/90 hover:bg-white/10'
                        }`}
                      >
                        {chip.icon ? `${chip.icon} ${chip.label}` : chip.label}
                      </span>
                    ))}
                  </div>
                  )}

                  {/* Actions - Ausgeblendet im ReadOnly */}
                  {!readOnly && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => onUsePrompt(item)}
                      className="flex-1 py-2.5 px-4 bg-[#10B981] hover:bg-[#059669] text-black font-bold text-sm rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#10B981]/20"
                      data-testid={`use-prompt-${item.id}`}
                    >
                      <CheckCircle className="w-4 h-4" />
                      Übernehmen
                    </button>
                    <button
                      onClick={() => onDeletePrompt(item.id)}
                      className="py-2.5 px-4 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 hover:text-red-300 font-bold text-sm rounded-lg transition-all flex items-center justify-center gap-2"
                      data-testid={`delete-prompt-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                      Löschen
                    </button>
                  </div>
                  )}
                </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PromptHistoryPanel;
